import { useState, useRef, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import {
  MarketAccessory,
  MarketButton,
  MarketHeader,
  MarketInputSearch,
  MarketList,
  MarketRow,
  MarketField,
} from '@market/react';
import { MarketInputSearchCustomEvent } from '@market/web-components/loader';
import { useDispatch, useSelector } from 'react-redux';
import { Customer } from '@squareup/sq-addons-web-sdk';
import { posContextSelector } from 'store/context';
import { ReactComponent as ArrowLeft } from '@market/icons/assets/svg/arrow-left.svg';
import { ReactComponent as Human } from '@market/icons/assets/svg/human.svg';

import {
  clearCustomersSearch,
  searchCustomers,
  searchValueSelector,
  searchResultsSelector,
  searchCustomersStateSelector,
} from 'store/searchCustomers';
import { RequestContext, addCustomer } from 'store/customer';
import { useAutoFocus } from 'hooks/useAutoFocus';
import ConnectionError from 'screens/ConnectionError';
import {
  CommonErrorCode,
  SquareLoyaltyApiError,
  SquareErrorCode,
} from 'store/api/errors';
import { formatPhoneNumber } from 'utils/phoneNumber';
import {
  CustomerDisplayNameType,
  getCustomerDisplayName,
} from 'utils/customer';

type CustomerDisplayImageProps = {
  initial?: string;
};

const CustomerDisplayImage = ({ initial }: CustomerDisplayImageProps) => {
  return (
    <MarketAccessory
      slot="leading-accessory"
      size="image"
      className="addons-market-accessory-customer-display-image"
    >
      <div className="addons-market-accessory-customer-display-image-content">
        {initial ?? (
          <Human
            className="addons-search-customer-display-image"
            width="24"
            height="24"
          />
        )}
      </div>
    </MarketAccessory>
  );
};

type SearchCustomerResultProps = {
  customer: Customer;
  onClickCustomer: (customer: Customer) => void;
};

const SearchCustomerResult = ({
  customer,
  onClickCustomer,
}: SearchCustomerResultProps) => {
  const hasName = () => {
    return customer.givenName && customer.familyName;
  };

  const getInitial = () => {
    if (hasName()) {
      return (
        (customer.givenName?.substring(0, 1) ?? '') +
        (customer.familyName?.substring(0, 1) ?? '')
      ).toUpperCase();
    }
  };

  const getSubtext = () => {
    if (customer.phoneNumber) {
      return formatPhoneNumber(customer.phoneNumber);
    }

    return customer.referenceId;
  };

  const initial = getInitial();

  return (
    <MarketRow
      className="noHover"
      data-testid="search-customer-result"
      onClick={() => {
        onClickCustomer(customer);
      }}
      variant="drill"
    >
      <CustomerDisplayImage initial={initial} />
      <label slot="label" style={{ margin: '5px 0' }}>
        {getCustomerDisplayName(customer, [CustomerDisplayNameType.NAME])}
      </label>
      <p slot="subtext">{getSubtext()}</p>
    </MarketRow>
  );
};

type SearchCustomerResultsProps = {
  customers?: Customer[];
  apiErrorCode?: SquareErrorCode;
};

const SearchCustomerResults = ({
  customers,
  apiErrorCode,
}: SearchCustomerResultsProps) => {
  const history = useHistory();
  const dispatch = useDispatch();

  if (!customers) {
    return null;
  }

  if (customers.length === 0 || apiErrorCode === CommonErrorCode.NOT_FOUND) {
    return (
      <div
        className="market-grid-item-full"
        data-testid="search-customers-no-customer-found"
        style={{ textAlign: 'center' }}
      >
        <strong>No customer found</strong>
        <p>Try searching by something else. Email must be exact match.</p>
      </div>
    );
  }

  const onClickCustomer = (customer: Customer) => {
    dispatch(addCustomer(customer, RequestContext.SEARCH_CUSTOMERS));
    history.push(`/customer` + history.location.search);
  };

  return (
    <MarketList className="market-grid-item-full" interactive slot={'content'}>
      {customers.map((customer: Customer) => {
        return (
          <SearchCustomerResult
            key={customer.referenceId}
            customer={customer}
            onClickCustomer={onClickCustomer}
          ></SearchCustomerResult>
        );
      })}
    </MarketList>
  );
};

const SearchCustomersScreen = () => {
  const posContext = useSelector(posContextSelector);
  const addonName = posContext?.configurations?.addonName || 'LAVA Loyalty';

  let history = useHistory();
  const dispatch = useDispatch();
  const autoFocus = useAutoFocus();

  const firstRender = useRef(true);

  const [searchValue, setSearchValue] = useState(
    useSelector(searchValueSelector)
  );

  const customers = useSelector(searchResultsSelector);
  const searchCustomersState = useSelector(searchCustomersStateSelector);

  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
    }
  }, []);

  const onClickSearchButton = async () => {
    dispatch(searchCustomers(searchValue));
  };

  const onMarketInputValueChange = (
    event: MarketInputSearchCustomEvent<{
      current: string;
    }>
  ) => {
    setSearchValue(event.detail.current);
  };

  const onBackButtonClick = () => {
    dispatch(clearCustomersSearch());
    history.goBack();
  };

  const handleKeyPress = (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.key === 'Enter' && !searchCustomersState.isPending) {
      (document.activeElement as HTMLElement)?.blur();
      onClickSearchButton();
    }
  };

  const getConnectionError = () => {
    return (
      <ConnectionError
        onClickTryAgain={() => {
          dispatch(searchCustomers(searchValue));
        }}
      />
    );
  };

  if (searchCustomersState.apiError) {
    if (searchCustomersState.apiError instanceof SquareLoyaltyApiError) {
      switch (searchCustomersState.apiError.code) {
        case CommonErrorCode.NOT_FOUND:
          break;
        default:
          return getConnectionError();
      }
    } else {
      return getConnectionError();
    }
  }

  return (
    <div className="market-grid-container">
      <MarketHeader className="market-grid-item-full addons-header" compact>
        <MarketButton
          data-testid="search-customers-back-btn"
          onClick={onBackButtonClick}
          rank="secondary"
          slot="navigation"
        >
          <div slot="icon" className="addons-icon">
            <ArrowLeft className="addons-back-icon" width="24" height="24" />
          </div>
        </MarketButton>
        <h2>Search {addonName} Members</h2>
        <MarketButton
          data-testid="search-customers-submit-btn"
          isLoading={searchCustomersState.isPending || undefined}
          onClick={onClickSearchButton}
          rank="primary"
          slot="actions"
        >
          Search
        </MarketButton>
      </MarketHeader>

      <MarketField
        className="market-grid-item-full"
        data-testid="search-customers-search-bar-field"
        invalid={!!searchCustomersState.validationError}
        name="search-customers-search-bar"
      >
        <MarketInputSearch
          ref={(ref) => {
            if (firstRender.current) {
              autoFocus(ref);
            }
          }}
          placeholder="Enter Email to find membership"
          data-testid="search-customers-search-bar-input"
          className="market-grid-item-full"
          value={searchValue}
          onMarketInputSearchValueChange={onMarketInputValueChange}
        >
          <input
            slot="input"
            onKeyDown={handleKeyPress}
            autoCorrect="off"
            autoCapitalize="none"
          />
        </MarketInputSearch>
        <small
          data-testid="search-customers-search-bar-helper"
          slot="helper"
          className="addons-helper-slot-text"
        >
          To locate a membership, you can search using the member's email address
        </small>
        <small data-testid="search-customers-search-bar-error" slot="error">
          Please enter a valid membership email.
          <div className="addons-helper-slot-text">Please enter an email.</div>
        </small>
      </MarketField>

      <SearchCustomerResults
        customers={customers}
        apiErrorCode={searchCustomersState.apiError?.code as SquareErrorCode}
      />
    </div>
  );
};

export default SearchCustomersScreen;
